import {
  Avatar,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  useUserContext,
  useUserContextDispatch,
} from "../../hooks/userContext";
import { useUserAvatar } from "../../hooks/useUserAvatar";
import { Link, useLocation } from "react-router";
import { HashLink } from "react-router-hash-link";
import "./styles.scss";
import { SiteLogo } from "../site-logo";
import { useEffect, useRef, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import classNames from "classnames";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { isAdminUser, Pages } from "../../utils";
import { PlanKey } from "../../pages/registration/homeowner-plan-selection-fieldset";
import { DesignerNavMenu } from "./designer-nav-menu";
import { AboutUsNavMenu } from "./about-us-nav-menu";
import { HomeownerNavMenu } from "./homeowner-nav-menu";

const navLinks = [
  { label: "About", to: Pages.COMING_SOON },
  { label: "How Matching Works", to: Pages.SMART_GUILD_MATCHING },
  { label: "The Guild Seal", to: `${Pages.ABOUT_US_PAGE}#the-seal` },
  { label: "Homeowner", to: Pages.HOMEOWNER_USER_LANDING },
  { label: "Designer", to: Pages.JOIN_AS_DESIGNER },
];

export const userMenuLinks = [
  { label: "My Dashboard", to: Pages.DASHBOARD },
  { label: "My Account", to: Pages.MY_ACCOUNT },
  { label: "My Application", to: Pages.DESIGNER_APPLICATION },
  { label: "Admin Dashboard", to: Pages.ADMIN_DASHBOARD },
];

export const SiteHeader = () => {
  const { user } = useUserContext();
  const { avatarUrl, initials } = useUserAvatar();
  const { logoutUserAccount } = useUserContextDispatch();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [aboutAnchor, setAboutAnchor] = useState<null | HTMLElement>(null);
  const [designerNavAnchor, setDesignerNavAnchor] = useState<null | HTMLElement>(null);
  const [homeownerNavAnchor, setHomeownerNavAnchor] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const { pathname, hash } = useLocation();
  const isAdmin = isAdminUser(user);
  const isDesigner = user?.accountType === "Designer";
  const isHomeowner = user?.accountType === "Homeowner";
  
  // Get user's subscription plan for homeowners
  const rawSubscription = typeof user?.subscription === 'string' 
    ? user.subscription 
    : (user?.subscription as any)?.homeownerValue ||
      (user?.subscription as any)?.designerValue ||
      (user?.subscription as any)?.value ||
      (user?.subscription as any)?.tier ||
      'basic';
  
  // Map GraphQL enum values to PlanKey type
  const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
    if (sub === 'Free' || sub === 'basic') return 'basic';
    if (sub === 'GuildMonthly' || sub === 'Guild') return 'Guild';
    if (sub === 'GuildAnnually' || sub === 'GuildPlus') return 'GuildPlus';
    if (sub === 'StyleFileMonthly' || sub === 'StyleFileAnnually') return 'Guild';
    return 'basic';
  };
  
  const userPlan: PlanKey = mapSubscriptionToPlanKey(rawSubscription);
  const isBasicPlan = isHomeowner && userPlan === 'basic';
  const isPremiumHomeowner = isHomeowner && (userPlan === 'Guild' || userPlan === 'GuildPlus');
  
  // Check if user has completed registration
  const hasCompletedRegistration = user?.onboardingStatus === 'active';
  
  // Only show user menu if registration is complete (or if admin)
  const shouldShowUserMenu = isAdmin || hasCompletedRegistration;

  
  const roleLabel = isAdmin
  ? "Administrator"
  : isDesigner
    ? "Designer"
    : "Homeowner";

  // Create dynamic menu for Basic plan homeowners
  const isDashboardOrProfile =
    pathname.startsWith(Pages.DASHBOARD) ||
    pathname.startsWith(Pages.PROFILE) ||
    pathname.startsWith(Pages.MY_ACCOUNT) ||
    pathname.startsWith(Pages.YOUR_HUB);
  
  // For Basic plan users: show "Manage Account" when not on dashboard, "Back to Website" when on dashboard
  const basicPlanLinks = isDashboardOrProfile 
    ? [{ label: "Back to Website", to: Pages.HOME }]
    : [{ label: "Manage Account", to: Pages.YOUR_HUB }];

  // For premium homeowners: simplified menu without "Edit Profile & Settings" and "My Application"
  const premiumHomeownerLinks = [
    { label: "My Dashboard", to: Pages.DASHBOARD },
  ];

  const userNavLinkList = isBasicPlan
    ? basicPlanLinks
    : isPremiumHomeowner
      ? premiumHomeownerLinks
      : isAdmin
        ? userMenuLinks
        : userMenuLinks.filter(link => link.label !== "Admin Dashboard");


  const open = Boolean(anchorEl);

  const handleAboutClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAboutAnchor(event.currentTarget);
  const handleDesignerNavClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setDesignerNavAnchor(event.currentTarget);
  const handleHomeownerNavClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setHomeownerNavAnchor(event.currentTarget);
  const handleAboutClose = () => setAboutAnchor(null);
  const handleHomeownerNavClose = () => setHomeownerNavAnchor(null);
  const handleMenuClose = () => setAnchorEl(null);

  const lastPathName = useRef(pathname);

  useEffect(() => {
    if (hash && lastPathName.current !== pathname) {
      lastPathName.current = pathname;
      setMobileMenuVisible(false);
    } else if (lastPathName.current !== pathname) {
      lastPathName.current = pathname;
      window.scrollTo(0, 0);
      setMobileMenuVisible(false);
    }
  }, [pathname, hash]);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <header className="site-header">
      <div className="site-wrapper">
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          style={{ height: "100%" }}
        >
          <Stack className="logo" direction="row" alignItems="center" spacing={2}>
            <Button
              color={"black" as any}
              variant="text"
              onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
              className="mobile-menu-button"
            >
              {mobileMenuVisible ? <CloseIcon /> : <MenuIcon />}
            </Button>
            <Link to={Pages.HOME} className="site-logo-link">
              <SiteLogo />
            </Link>
          </Stack>

          <nav className="nav-links">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack className="desktop-nav" direction="row" alignItems="center" spacing={1}>
                {navLinks.map((link) =>
                  link.label === "About" ? (
                    <Button key={link.label} className="btn-text font-size-3" endIcon={!!aboutAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={handleAboutClick}>
                      {link.label}
                    </Button>
                  ) : link.label === "The Guild Seal" ? (
                    <Button
                      key={link.label}
                      className="btn-text font-size-3"
                      component={HashLink}
                      to={link.to}
                    >
                      {link.label}
                    </Button>
                  ) : link.label === "Designer" ? (
                    <Button key={link.label} className="btn-text font-size-3" endIcon={!!designerNavAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={handleDesignerNavClick}>
                      {link.label}
                    </Button>
                  ) : link.label === "Homeowner" ? (
                    <Button key={link.label} className="btn-text font-size-3" endIcon={!!homeownerNavAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={handleHomeownerNavClick}>
                      {link.label}
                    </Button>
                  ) : (
                    <Button key={link.label} component={Link} to={link.to} className="btn-text font-size-3">
                      {link.label}
                    </Button>
                  )
                )}

                {/* Show Login/Register buttons if no user OR if user hasn't completed registration */}
                {(!user || !shouldShowUserMenu) && (
                  <>
                    <Link to={Pages.LOGIN}>
                      <Button className="btn-outline font-size-3" variant="outlined">Login</Button>
                    </Link>
                    <Link to={Pages.SIGNUP}>
                      <Button className="btn-fill font-size-3" variant="contained">Sign Up</Button>
                    </Link>
                  </>
                )}
              </Stack>
              {/* Only show user menu dropdown if registration is complete */}
              {user && shouldShowUserMenu && (
                <>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} className="user-menu-button">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar src={avatarUrl} sx={{ width: 32, height: 32 }}>
                        {(initials ?? user.firstName?.[0] ?? "").toUpperCase()}
                      </Avatar>
                      <Typography className="user-name">
                        {user.firstName} {user.lastName?.[0]}.
                      </Typography>
                      <ExpandMoreIcon />
                    </Stack>
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    PaperProps={{ className: "dashboard-topbar-menu" }}
                  >
                    <Box className="menu-header">
                      <Typography className="menu-title">Account</Typography>
                      <Typography className="menu-name">{user.firstName} {user.lastName}</Typography>
                      <Typography className="menu-role">{roleLabel}</Typography>

                      {isMobile && isDashboardOrProfile && !isBasicPlan && (
                        <MenuItem
                          className="back-to-website"
                          onClick={() => {
                            handleMenuClose();
                            window.open(Pages.HOME, "_blank");
                          }}
                        >
                          Back to Website
                          <OpenInNewIcon fontSize="small" />
                        </MenuItem>
                      )}
                    </Box>
                    <Divider />
                    {(isMobile && isDashboardOrProfile
                      ? (
                          isAdmin
                            ? [
                                { label: "Manage Account", to: Pages.MY_ACCOUNT },
                                { label: "Admin Dashboard", to: Pages.ADMIN_DASHBOARD },
                              ]
                            : isDesigner
                              ? [
                                  { label: "My Application", to: Pages.DESIGNER_APPLICATION },
                                  { label: "Manage Account", to: Pages.MY_ACCOUNT },
                                ]
                              : isBasicPlan
                                ? basicPlanLinks
                                : isPremiumHomeowner
                                  ? [{ label: "Manage Account", to: Pages.MY_ACCOUNT }]
                                  : [{ label: "Manage Account", to: Pages.MY_ACCOUNT }]
                        )
                      : userNavLinkList
                    ).map((link) => (
                      <MenuItem key={link.label} onClick={() => {
                        handleMenuClose();
                        window.location.href = link.to;
                      }}>
                        {link.label}
                      </MenuItem>
                    ))}
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      logoutUserAccount();
                    }}>
                      Sign Out
                    </MenuItem>
                  </Menu>
                </>
              )}


            </Stack>

            {!user && (
              <Link to={Pages.LOGIN}>
                <Button variant="text" className="btn-text text-center mobile-signup-btn font-size-3">
                  <Stack justifyContent="center" alignItems="center">
                    <PersonIcon />
                    <span>Sign In</span>
                  </Stack>
                </Button>
              </Link>
            )}
          </nav>
        </Stack>

        <nav className={classNames("mobile-nav", mobileMenuVisible && "visible")}>
          <Stack direction="row" className="close-btn-container">
            <Button variant="text" onClick={() => setMobileMenuVisible(false)} className="close-button btn-text">
              <CloseIcon />
            </Button>
          </Stack>
          <Stack className="text-center" direction="column">
            {navLinks.map((link) => {
              const isDropdown = ["About", "Designer", "Homeowner"].includes(link.label);

              if (isDropdown) {
                return (
                  <Button
                    key={link.label}
                    className="mobile-link btn-text thin"
                    variant="text"
                    onClick={
                      link.label === "About"
                        ? handleAboutClick
                        : link.label === "Designer"
                        ? handleDesignerNavClick
                        : handleHomeownerNavClick
                    }
                    endIcon={
                      (!!designerNavAnchor && link.label === "Designer") ||
                      (!!aboutAnchor && link.label === "About") ||
                      (!!homeownerNavAnchor && link.label === "Homeowner") ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )
                    }
                  >
                    {link.label}
                  </Button>
                );
              } else {
                const isSealLink = link.label === "The Guild Seal";
                return (
                  <Button
                    key={link.label}
                    component={isSealLink ? HashLink : Link}
                    to={link.to}
                    className="mobile-link btn-text thin"
                    variant="text"
                  >
                    {link.label}
                  </Button>
                );
              }
            })}
          </Stack>
        </nav>
      </div>

      <DesignerNavMenu anchor={designerNavAnchor} onClose={() => setDesignerNavAnchor(null)} />
      <AboutUsNavMenu anchor={aboutAnchor} onClose={handleAboutClose} />
      <HomeownerNavMenu anchor={homeownerNavAnchor} onClose={handleHomeownerNavClose} />
    </header>
  );
};
